import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Mail, ArrowLeft, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

import API_URL from '../config/api';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setSent(true);
      toast.success('Reset link sent! Check your email.');
    } catch (err) {
      const message = err.response?.data?.detail || 'Something went wrong';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-6">
        <div className="w-full max-w-md text-center animate-fade-in">
          <div className="w-20 h-20 rounded-full bg-[#2D6A4F]/10 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-[#2D6A4F]" />
          </div>
          <h1 className="heading-2 mb-4">Check Your Email</h1>
          <p className="body-text text-[#6C6C67] mb-8">
            We've sent a password reset link to <strong>{email}</strong>. 
            Please check your inbox and follow the instructions.
          </p>
          <Link to="/login">
            <Button className="btn-secondary" data-testid="back-to-login-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Login
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-6">
      <div className="w-full max-w-md animate-fade-in">
        <Link to="/" className="flex items-center gap-2 mb-12" data-testid="forgot-password-logo">
          <Heart className="w-8 h-8 text-[#6E1A24]" strokeWidth={1.5} />
          <span className="text-2xl font-serif font-medium text-[#2D2D2A]">LusoLove</span>
        </Link>
        
        <Link to="/login" className="inline-flex items-center text-sm text-[#6C6C67] hover:text-[#2D2D2A] mb-8" data-testid="back-link">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to login
        </Link>
        
        <h1 className="heading-2 mb-2">Forgot Password?</h1>
        <p className="body-text text-[#6C6C67] mb-8">
          No worries! Enter your email and we'll send you a reset link.
        </p>
        
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm" data-testid="forgot-password-error">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="label-text block mb-2">Email</label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6C6C67]" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white border border-[#E5E3DB] rounded-xl focus:outline-none focus:border-[#6E1A24] transition-colors"
                placeholder="your@email.com"
                required
                data-testid="forgot-password-email"
              />
            </div>
          </div>
          
          <Button 
            type="submit" 
            className="btn-primary w-full flex items-center justify-center gap-2"
            disabled={loading}
            data-testid="forgot-password-submit"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Sending...
              </>
            ) : (
              'Send Reset Link'
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
